"use client"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import dynamic from "next/dynamic"
import { Fragment, useState } from "react"
import { useInView } from "react-intersection-observer"
import { TransitionNumbersCounter, useInViewConfig } from "ui"
import animationData from "ui/LottieAnimations/Number-Of-Interest-Dot.json"
import { ProjectPage } from "utils"
import TimerPopup from "./TimerPopup"

// Dynamically import Lottie to avoid SSR issues
const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

type Props = {
  powerNumbers: ProjectPage["numbersNew"]
  rentProjectFOMO?: ProjectPage["rentProjectFOMO"]
  locale: string
  ready: boolean
  animFunction: string
  mobileAnimFunction: string
  shouldRenderTimer?: boolean
}

const Container = styled.div<{
  locale: string
  ready: boolean
  animFunction: string
  mobileAnimFunction: string
}>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 100%;
  /* min-height: 116px; */
  border-radius: 12px;
  box-shadow: 0 0 14.1px 2px rgba(31, 55, 93, 0.1);
  backdrop-filter: blur(3.299999952316284px);
  background-color: white;

  transform: translate(-50%, 200%) scale(0.98);
  opacity: 0;

  position: relative;

  animation: ${(props) => props.mobileAnimFunction};
  animation-play-state: ${(props) => (props.ready ? "running" : "paused")};
  animation-name: slide-from-bottom-mobile;

  will-change: transform, opacity;
  transform: translate(-50%, 200%) scale(0.98);

  ${(props) => props.theme.breakpoints.up("lg")} {
    position: absolute;
    top: calc(100% - 155px);

    width: 100%;
    max-width: 1200px;
    padding: 0;
    overflow: hidden;
    box-shadow: 0 0 12.9px 2px rgba(154, 153, 153, 0.3);
    z-index: 99;
    left: 50%;
    border: 1px solid ${(props) => props.theme.colors.darkBlue4rentMarketing};

    animation: ${(props) => props.animFunction};
    animation-play-state: ${(props) => (props.ready ? "running" : "paused")};
    animation-name: slide-from-bottom;
  }

  @keyframes slide-from-bottom {
    0% {
      transform: translate(-50%, 200%) scale(0.98);
      opacity: 0;
    }
    100% {
      transform: translate(-50%, 0%) scale(1);
      opacity: 1;
    }
  }
  @keyframes slide-from-bottom-mobile {
    0% {
      transform: translate(0%, 200%) scale(0.98);
      opacity: 0;
    }
    100% {
      transform: translate(0%, 0%) scale(1);
      opacity: 1;
    }
  }
`

const PowerNumberWrapper = styled.ul`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;

  gap: 1px;
  padding: 2px 0px;
  margin: 0;
  ${(props) => props.theme.breakpoints.up("sm")} {
    padding: 2px 16px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding: 8px 24px;
    gap: 24px;
  }
`

const PowerNumberItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  flex: 1;
  text-align: center;
  justify-content: flex-start;
  height: 100%;
  gap: 8px;
  align-self: stretch;
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 0;
    padding-block: 24px;
  }
  strong {
    font-size: 30px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
    ${(props) => props.theme.breakpoints.up("lg")} {
      font-size: 60px;
    }
  }
  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 150%; /* 36px */
    letter-spacing: -0.48px;
    margin: 0;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    white-space: normal;

    ${(props) => props.theme.breakpoints.up("lg")} {
      font-size: 24px;
      font-weight: 300;
    }
  }
`

const DividerLine = styled.div`
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  width: 1px;
  background-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  transform: rotate(15deg);
  margin-inline: 20px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-inline: 0;
  }
`

const PowerNumbers = ({
  powerNumbers,
  rentProjectFOMO,
  locale,
  ready,
  animFunction,
  mobileAnimFunction,
  shouldRenderTimer,
}: Props) => {
  const { ref, inView } = useInView(useInViewConfig)
  const [hideTimerPopUp, setHideTimerPopUp] = useState<boolean>(false)
  const defaultOptions = {
    animationData: animationData,
    loop: true,
  }
  const handleHide = () => {
    setHideTimerPopUp(true)
  }
  return powerNumbers?.[0]?.number || (!!rentProjectFOMO?.timer && !hideTimerPopUp) ? (
    <Container
      locale={locale}
      ready={ready}
      animFunction={animFunction}
      mobileAnimFunction={mobileAnimFunction}
    >
      {shouldRenderTimer && rentProjectFOMO?.timer && !hideTimerPopUp && (
        <TimerPopup
          locale={locale}
          rentProjectFOMO={rentProjectFOMO}
          handleHide={handleHide}
          forDesktop={true}
        />
      )}
      {powerNumbers?.[0]?.number && (
        <PowerNumberWrapper ref={ref}>
          {powerNumbers?.map(({ number, text, textNextToNumber }, index) => {
            return (
              <Fragment key={index}>
                <PowerNumberItem>
                  <strong>
                    <Box
                      component="span"
                      position="relative"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      {index === 0 && (
                        <Box
                          sx={{
                            "--w": "32.18px",
                            "--w-mobile": "10px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: { xs: "var(--w-mobile)", md: "var(--w)" },
                            height: { xs: "var(--w-mobile)", md: "var(--w)" },
                            fontSize: 0,
                            "& > div": {
                              width: "100%",
                              height: "100%",
                              display: "flex",
                            },
                          }}
                        >
                          <Lottie
                            animationData={defaultOptions.animationData}
                            loop={defaultOptions.loop}
                          />
                        </Box>
                      )}
                      <TransitionNumbersCounter inView={inView} number={number!} />
                    </Box>{" "}
                    {textNextToNumber}
                  </strong>
                  <p>{text}</p>
                </PowerNumberItem>
                {index < powerNumbers.length - 1 && <DividerLine />}
              </Fragment>
            )
          })}
        </PowerNumberWrapper>
      )}
    </Container>
  ) : null
}
export default PowerNumbers
